import { KeyIcon, PencilIcon, EyeIcon, DocumentTextIcon, ExclamationTriangleIcon, ArrowPathIcon, DocumentArrowDownIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import SelectedDocument from "../SelectedDocument";
import { Customer } from "types/customer";
import { Quotation } from "types/quotation";
import { useLanguage } from "contexts/LanguageContext";
import { Template } from "types/template";
import { useRef, useEffect, useState } from "react";
import { renderAsync } from 'docx-preview';
import toast from "react-hot-toast";
import { error } from "console";
import { useStorage } from "hooks/useStorage";
import { DocGenerator } from "utils/docGenerator";
import DocumentPreview from "../DocumentPreview";
import PodTableViewer from "../PodTableViewer";

interface EditDataPageProps {
  documentDataProvider: Quotation | Customer | null;
  sortedDocumentData: string[][];
  filledPercentage: number;
  filledCount: number;
  templateFields: string[];
  documentData: Record<string, string>;
  editingKey: string;
  editingValue: string;
  setEditingValue: (value: string) => void;
  handleSaveEdit: () => void;
  handleCancelEdit: () => void;
  handleEdit: (key: string, value: string) => void;
  selectedTemplate: Template | null;
  closePreview: () => void;
}

// Fogyasztás mező formázása ezres tagolással (csak megjelenítéshez)
const formatConsumption = (value: string): string => {
  if (!value) return '';
  
  // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
  const cleanValue = value.replace(/[^0-9.]/g, '');
  
  // Szétválasztjuk a tizedes rész előtti és utáni részre
  const parts = cleanValue.split('.');
  
  // Formázzuk az egész részt ezres tagolással
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Összerakjuk az eredményt
  return parts.length > 1 ? parts.join('.') : parts[0];
};

// Ellenőrzi, hogy a mező fogyasztással kapcsolatos-e
const isConsumptionField = (key: string): boolean => {
  return key === 'Fogyasztás kWh' || 
         key.includes('MÉF') || 
         key.includes('Éves mennyiség') ||
         key.includes('fogyasztás') ||
         key === 'Szerződéses ár' ||
         key === 'Ajánlati ár';
};

// Ellenőrzi, hogy a mező fizetési határidő-e
const isPaymentDueField = (key: string): boolean => {
  return key === 'Fizetési határidő';
};

// Ellenőrzi, hogy a mező az ajánlati ár egysége-e
const isPriceUnitField = (key: string): boolean => {
  return key === 'Ajánlati ár HUF/kWh-EUR/ MWh' || 
         key === 'Ajánlati ár HUF/kWh-EUR/MWh' || 
         key === 'priceUnit';
};

// Fizetési határidő értékének előkészítése szerkesztéshez
const preparePaymentDueForEdit = (value: string): string => {
  if (!value) return '';
  return value.replace(/ nap$/, '');
};

// Ajánlati ár egységének előkészítése szerkesztéshez (ha szükséges)
const preparePriceUnitForEdit = (value: string): string => {
  if (!value) return '';
  return value;
};

const EditDataPage = ({
  documentDataProvider,
  sortedDocumentData,
  filledPercentage,
  filledCount,
  templateFields,
  documentData,
  editingKey,
  editingValue,
  setEditingValue,
  handleSaveEdit,
  handleCancelEdit,
  handleEdit,
  selectedTemplate,
  closePreview
}: EditDataPageProps) => {
  const { t } = useLanguage();
  const { uploadFile } = useStorage();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isPodTableViewerOpen, setIsPodTableViewerOpen] = useState(false);
  const [filteredTemplateFields, setFilteredTemplateFields] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const previewFileNameRef = useRef<string>(`preview_${Date.now()}.docx`);

  // Custom edit handler to remove "nap" suffix from Fizetési határidő
  const handleEditField = (key: string, value: string) => {
    let cleanValue = value;
    
    // Remove "nap" from Fizetési határidő value if it appears at the end
    if (isPaymentDueField(key) && typeof value === 'string' && value.trim().endsWith(' nap')) {
      cleanValue = value.replace(/ nap$/, '');
    }
    
    // Special handling for price unit field if needed
    if (isPriceUnitField(key)) {
      console.log("Editing price unit field:", key, value);
      console.log("Raw value type:", typeof value, "Raw value:", value);
      
      // Try to find a valid price unit value from any of the possible fields
      const priceUnitKeys = ['priceUnit', 'Ajánlati ár HUF/kWh-EUR/MWh', 'Ajánlati ár HUF/kWh-EUR/ MWh'];
      let foundValue = value;
      
      // Check if current value is valid
      if (value === 'HUF/kWh' || value === 'EUR/MWh') {
        console.log("Valid price unit value found in current field:", value);
        foundValue = value;
      } 
      // Otherwise check if value exists in any of the other price unit fields
      else {
        for (const priceKey of priceUnitKeys) {
          if (documentData[priceKey] && 
              (documentData[priceKey] === 'HUF/kWh' || documentData[priceKey] === 'EUR/MWh')) {
            foundValue = documentData[priceKey];
            console.log(`Found valid price unit value in ${priceKey}:`, foundValue);
            break;
          }
        }
      }
      
      // If we found a valid value, use it
      if (foundValue === 'HUF/kWh' || foundValue === 'EUR/MWh') {
        console.log("Using valid price unit value:", foundValue);
        cleanValue = foundValue;
        // Set the editing value directly to ensure it's selected in the dropdown
        setTimeout(() => setEditingValue(foundValue), 0);
      } 
      // If no valid value was found, default to HUF/kWh
      else {
        console.log("No valid price unit value found, defaulting to HUF/kWh");
        cleanValue = 'HUF/kWh';
        // Set the editing value directly to ensure it's selected in the dropdown
        setTimeout(() => setEditingValue('HUF/kWh'), 0);
      }
    }
    
    handleEdit(key, cleanValue);
  };

  // Filter out POD_TABLE from display and apply search filter
  useEffect(() => {
    let filtered = templateFields.filter(key => 
      key !== 'POD_TABLE' && 
      key !== 'K épviselő neve' // Szűrjük ki a hibás képviselő mezőt
    );
    
    // Always include Fizetési határidő if documentData exists for it
    const hasPaymentDue = documentData['Fizetési határidő'] && documentData['Fizetési határidő'].trim() !== '';
    const hasPaymentDueField = filtered.includes('Fizetési határidő');
    
    if (hasPaymentDue && !hasPaymentDueField) {
      filtered.push('Fizetési határidő');
    }
    
    // Always include price unit field if it exists in the document data
    const priceUnitKey = 'Ajánlati ár HUF/kWh-EUR/ MWh';
    const altPriceUnitKey = 'Ajánlati ár HUF/kWh-EUR/MWh';
    const directPriceUnitKey = 'priceUnit';
    
    const hasPriceUnit = documentData[priceUnitKey] !== undefined || 
                        documentData[altPriceUnitKey] !== undefined || 
                        documentData[directPriceUnitKey] !== undefined;
    
    const hasPriceUnitField = filtered.includes(priceUnitKey) || 
                             filtered.includes(altPriceUnitKey) || 
                             filtered.includes(directPriceUnitKey);
    
    if (hasPriceUnit && !hasPriceUnitField) {
      // Check which key exists in document data
      if (documentData[priceUnitKey] !== undefined) {
        filtered.push(priceUnitKey);
      } else if (documentData[altPriceUnitKey] !== undefined) {
        filtered.push(altPriceUnitKey);
      } else if (documentData[directPriceUnitKey] !== undefined) {
        filtered.push(directPriceUnitKey);
      }
    }
    
    if (searchQuery) {
      filtered = filtered.filter(key => 
        key.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (documentData[key] && documentData[key].toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Debug logging
    console.log('Template fields:', templateFields);
    console.log('Document data keys:', Object.keys(documentData));
    console.log('Payment due field value:', documentData['Fizetési határidő']);
    console.log('Price unit field value:', documentData['Ajánlati ár HUF/kWh-EUR/ MWh']);
    console.log('Alt price unit field value:', documentData['Ajánlati ár HUF/kWh-EUR/MWh']);
    console.log('Direct price unit field value:', documentData['priceUnit']);
    console.log('Filtered fields:', filtered);
    
    setFilteredTemplateFields(filtered);
  }, [templateFields, searchQuery, documentData]);

  // Clean the editingValue when it's the Fizetési határidő field
  useEffect(() => {
    if (isPaymentDueField(editingKey) && typeof editingValue === 'string' && editingValue.trim().endsWith(' nap')) {
      const cleanValue = editingValue.replace(/ nap$/, '');
      setEditingValue(cleanValue);
    }
    
    // Log the current editing key and value for debugging
    if (editingKey) {
      console.log("Currently editing:", editingKey, "with value:", editingValue);
      
      // Special handling for price unit field
      if (isPriceUnitField(editingKey)) {
        console.log("Editing price unit field with value:", editingValue);
      }
    }
  }, [editingKey, editingValue]);

  const generatePreview = async () => {
    if (!selectedTemplate || !selectedTemplate.fileUrl) return;

    setIsGeneratingPreview(true);
    setIsLoadingPreview(true);

    try {
      // Generálunk egy dokumentumot a behelyettesített értékekkel
      const dataToUse = [documentData];

      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        dataToUse,
        ['docx']
      );

      if (generatedDocs['docx'] && generatedDocs['docx'].length > 0) {
        const generatedDocumentBlob = generatedDocs['docx'][0];
        // Új fájlnév-referencia generálása
        previewFileNameRef.current = `preview_${Date.now()}.docx`;

        // Feltöltjük a generált dokumentumot
        const fileUrl = await uploadFile(
          new File([generatedDocumentBlob], previewFileNameRef.current, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }),
          'previews'
        );

        if (fileUrl) {
          // Közvetlenül a Firebase Storage URL-t használjuk
          setPreviewUrl(fileUrl);
          setShowPreview(true);
        } else {
          throw new Error("Nem sikerült feltölteni az előnézeti dokumentumot");
        }
      } else {
        throw new Error("Nem sikerült generálni az előnézeti dokumentumot");
      }
    } catch (error) {
      console.error("Hiba az előnézet generálása közben:", error);
      toast.error(t("workPage.messages.previewError"));
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Handle opening the POD table viewer
  const handleOpenPodTableViewer = () => {
    if (documentData['POD_TABLE']) {
      setIsPodTableViewerOpen(true);
    } else {
      toast.error(t("documents.noPodTableData"));
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-auto lg:h-[650px]">
      {/* Értékek szerkesztése panel */}
      <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 flex-1 flex flex-col max-h-full mb-4 lg:mb-0 shadow-sm">
        {documentDataProvider && <SelectedDocument documentDataProvider={documentDataProvider} />}
        <h3 className="text-sm font-medium text-gray-800 mb-2 sm:mb-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
          <span>{t('documents.editValues')}</span>
          <div className="flex items-center space-x-3 text-xs">
            <div className="flex items-center text-gray-500">
              <KeyIcon className="h-3.5 w-3.5 mr-1" />
              <span>{t('documents.fieldsInTemplate', { count: sortedDocumentData.length })}</span>
            </div>
            <div className="flex items-center">
              <span className={`${filledPercentage > 70 ? 'text-green-600' : filledPercentage > 30 ? 'text-amber-600' : 'text-red-600'}`}>
                {filledCount} / {sortedDocumentData.length} {t('documents.filled')} ({filledPercentage}%)
              </span>
            </div>
          </div>
        </h3>

        {/* POD Table Button and Search */}
        <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            placeholder={t('documents.searchField')}
            className="flex-1 bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-800 placeholder-gray-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          {documentData['POD_TABLE'] && (
            <button
              onClick={handleOpenPodTableViewer}
              className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              <TableCellsIcon className="h-4 w-4 mr-1" />
              <span className="text-sm">{t('documents.viewPodTable')}</span>
            </button>
          )}
        </div>

        {/* Mező-érték lista */}
        <div className="flex-1 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-teal-500/50">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 py-2 pl-2">{t('documents.field')}</th>
                <th className="text-left text-xs font-medium text-gray-500 py-2 pl-2">{t('documents.value')}</th>
                <th className="w-12 text-right text-xs font-medium text-gray-500 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTemplateFields.map((key) => {
                let value = documentData[key] || '';
                let displayValue: string | JSX.Element = value;
                
                // Fizetési határidő előkészítése szerkesztéshez
                if (isPaymentDueField(key) && typeof value === 'string' && value.trim().endsWith(' nap')) {
                  value = preparePaymentDueForEdit(value);
                }
                
                // Formázott érték megjelenítése fogyasztás mezőknél
                if (isConsumptionField(key) && value) {
                  displayValue = formatConsumption(value);
                }
                
                // Special handling for price unit display if needed
                if (isPriceUnitField(key)) {
                  // Check all possible fields for a valid value
                  const priceUnitKeys = ['priceUnit', 'Ajánlati ár HUF/kWh-EUR/MWh', 'Ajánlati ár HUF/kWh-EUR/ MWh'];
                  let validValue = value;
                  
                  if (value !== 'HUF/kWh' && value !== 'EUR/MWh') {
                    // Try to find a valid value in any of the price unit fields
                    for (const priceKey of priceUnitKeys) {
                      if (documentData[priceKey] && 
                          (documentData[priceKey] === 'HUF/kWh' || documentData[priceKey] === 'EUR/MWh')) {
                        validValue = documentData[priceKey];
                        break;
                      }
                    }
                  }
                  
                  // If field is empty or not a valid option, show helper text
                  if (!validValue || (validValue !== 'HUF/kWh' && validValue !== 'EUR/MWh')) {
                    displayValue = <span className="text-gray-400 italic">Válasszon: HUF/kWh vagy EUR/MWh</span>;
                    // Also add a highlight to indicate it needs attention
                    displayValue = <span 
                      onClick={() => handleEditField(key, value)} 
                      className="bg-yellow-50 px-2 py-1 rounded border border-yellow-200 inline-block cursor-pointer hover:bg-yellow-100 transition-colors flex items-center"
                    >
                      {displayValue}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>;
                  } else {
                    // Add a highlight for valid values
                    displayValue = <span 
                      onClick={() => handleEditField(key, validValue)} 
                      className="bg-green-50 px-2 py-1 rounded border border-green-200 inline-block cursor-pointer hover:bg-green-100 transition-colors flex items-center"
                    >
                      {validValue}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>;
                  }
                }
                
                return (
                  <tr key={key} className="hover:bg-gray-50">
                    {editingKey === key ? (
                      <>
                        <td className="py-2 pl-2 align-top font-medium text-teal-600 text-xs sm:text-sm">{key}</td>
                        <td className="py-2 pl-2">
                          {isPriceUnitField(key) ? (
                            <select 
                              value={editingValue || ''}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-800"
                            >
                              <option value="">Válasszon...</option>
                              <option value="HUF/kWh">HUF/kWh</option>
                              <option value="EUR/MWh">EUR/MWh</option>
                            </select>
                          ) : (
                            <textarea
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-full h-24 bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-800"
                            />
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={handleSaveEdit}
                              className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 rounded-full hover:bg-red-100 text-red-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 pl-2 font-medium text-gray-700 text-xs sm:text-sm">{key}</td>
                        <td className="py-2 pl-2 text-gray-600 whitespace-pre-wrap">
                          {typeof displayValue === 'object' ? displayValue : (displayValue || <span className="text-gray-400 italic">{t('documents.empty')}</span>)}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => handleEditField(key, value)}
                            className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sablon előnézet panel */}
      <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 lg:w-1/2 w-full flex flex-col min-h-[400px] lg:min-h-0 lg:max-h-full shadow-sm">
        <div className="text-sm font-medium text-gray-800 mb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div className="flex items-center justify-end w-full space-x-2">
            <button
              onClick={generatePreview}
              disabled={isGeneratingPreview || !selectedTemplate}
              className={`flex items-center text-xs rounded-md px-2 py-1 ${isGeneratingPreview || !selectedTemplate
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
            >
              {isGeneratingPreview ? (
                <>
                  <LoadingSpinner size="sm" className="mr-1" />
                  <span>{t('documents.generating')}</span>
                </>
              ) : (
                <>
                  <EyeIcon className="h-3.5 w-3.5 mr-1" />
                  <span>{showPreview ? t('documents.regeneratePreview') : t('documents.generatePreview')}</span>
                </>
              )}
            </button>
          </div>

        </div>
        <DocumentPreview
          selectedTemplate={previewUrl ? {
            fileUrl: previewUrl,
            fileName: previewFileNameRef.current
          } : null}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      </div>

      {/* POD Table Viewer Modal */}
      <PodTableViewer
        isOpen={isPodTableViewerOpen}
        onClose={() => setIsPodTableViewerOpen(false)}
        podTableData={documentData['POD_TABLE'] || ''}
      />
    </div>
  );
};

export default EditDataPage;
